<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { FolderDot, Eye, Trash2, Pencil, Share, Ellipsis, Layers, Sparkles } from 'lucide-vue-next'
import { VerifiedMarker } from '@/components/ui/verified-marker'

import ReportDataList from './ReportDataList.vue'
import Monitoring from './Monitoring.vue'
import FeatureThread from '@/components/discussion/FeatureThread.vue'
import GeofenceEventList from './GeofenceEventList.vue'
import Weather from './Weather.vue'
import Elevation from './Elevation.vue'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DialogConfirm } from '@/components/dialogs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Point, LineString, Polygon, MultiPolygon } from '@/components/icons'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { MediaList } from '@/components/media'

import { bbox, area } from '@turf/turf'
import { hasAreaGeometry } from '@/utils/geometry'
import { formatTime } from '@/utils/datetime'
import { API, HOST } from '@/utils/http'
import { useToast } from '@/components/ui/toast/use-toast'

const { toast } = useToast()
const router = useRouter()
const route = useRoute()
const store = useStore()
const dialogDeleteConfirm = ref(null)
const isAddingToDataTab = ref(false)

const geofenceEvents = ref(null)

const projects = computed(() => store.getters['project/getProjects'])
const feature = computed(() => store.getters['geo/getCurrentFeature'])
const settings = computed(() => store.getters['user/getUserSettings'])
const map = computed(() => store.getters['geo/getMap'])

const areaSize = computed(() => {
  if (!feature.value) return
  if (!hasAreaGeometry(feature.value.geometry)) return

  const areaInSm = area(feature.value)
  const areaInHa = areaInSm / 10000 // 1 ha = 10000 sm
  const areaInSkm = areaInSm / 1000000 // 1 skm = 1000000 sm
  return {
    sm: (areaInSm).toFixed(4),
    ha: (areaInHa).toFixed(4),
    skm: (areaInSkm).toFixed(4)
  }
})

const featureCode = computed(() => route.params.featureCode)

const projectName = computed(() => {
  const id = feature.value.properties.project_id
  const name = projects.value.find(p => p.project_id === id)?.project_name
  return name ? name : id
})

const iconName = computed(() => {
  return feature.value.geometry.type.toLowerCase()
})

const createdOrigin = computed(() => {
  let created_origin = feature.value.properties.created_origin
  let created_at = feature.value.properties.created_at
  if (created_origin) {
    if (created_origin.toString().length === 10) {
      created_origin = created_origin * 1000
    }

    return formatTime(created_origin, settings.value.tz)
  } else {
    return formatTime(created_at, settings.value.tz)
  }
})

const createdAt = computed(() => {
  return formatTime(createdOrigin.value, settings.value.tz, `MMMM D YYYY, ${settings.value.timeFormat === '12' ? 'h:mm a' : 'H:mm'}`)
})

const syncedAt = computed(() => {
  return formatTime(feature.value.properties.created_at, settings.value.tz, `MMMM D YYYY, ${settings.value.timeFormat === '12' ? 'h:mm a' : 'H:mm'}`)
})

const updatedAt = computed(() => {
  return formatTime(feature.value.properties.updated_at, settings.value.tz, `MMMM D YYYY, ${settings.value.timeFormat === '12' ? 'h:mm a' : 'H:mm'}`)
})


const copyUrl = () => {
  const url = router.resolve({name: 'Feature', params: {project_id: feature.value.properties.project_id, featureCode: feature.value.properties.code}}).href
  const fullUrl = window.location.origin + url
  navigator.clipboard.writeText(fullUrl)
  toast({title: 'Share link copied to clipboard'})
}

const focusOnFeature = () => {
  if (feature.value.geometry.type === 'Point') {
    map.value.flyTo({
      center: feature.value.geometry.coordinates,
      zoom: 15
    })
  } else {
    map.value.fitBounds(bbox(feature.value.geometry))
  }
}

const showLeftDialog = () => {
  router.push({name: 'FeatureEdit', params: {featureCode: feature.value.properties.code}})
  focusOnFeature()
}

const exportGeojson = () => {
  const geoJSON = {
    type: 'FeatureCollection',
    features: [feature.value]
  }
  const fileName = feature.value.properties.name
  const geoJSONString = JSON.stringify(geoJSON, null, 2)
  const blob = new Blob([geoJSONString], { type: "application/json" })
  const link = document.createElement('a')

  link.href = URL.createObjectURL(blob)
  link.download = fileName + '.geojson'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const fetchGeofenceEvents = async () => {
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/geo/feature/${feature.value.properties.code}/geofence`
    })

    geofenceEvents.value = response.data.reverse()
  } catch (e) {
    console.log('Error fetching geofence events')
  }
}

const deleteReport = () => {
  store.dispatch('geo/deleteFeature', feature.value.properties.code)
  router.push({name: 'ProjectHome', params: {projectId: feature.value.properties.project_id}})
}

const addToDataTab = async () => {
  if (isAddingToDataTab.value || !feature.value) return

  isAddingToDataTab.value = true
  try {
    const orgId = store.getters['org/getSelectedOrg'].organization_id
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${orgId}/layers`,
      data: {
        org_id: parseInt(orgId),
        type: 5,
        name: feature.value.properties.name,
        layer: {
          data: {
            feature_ids: [feature.value.properties.code]
          }
        }
      }
    })

    toast({
      title: 'Added to Data tab',
      description: `Static layer "${feature.value.properties.name}" created`
    })
  } catch (error) {
    console.error('Error creating static layer:', error)
    toast({
      title: 'Error',
      description: error.response?.data?.detail || 'Could not add to Data tab',
      variant: 'destructive'
    })
  }
  isAddingToDataTab.value = false
}

const handleResourceDelete = async () => {
  await store.dispatch('geo/fetchFeature', featureCode.value)
}

const aiEnabled = computed(() => !!store.getters['org/getSelectedOrg']?.features?.ai_enabled)

const featureSummary = computed(() => feature.value?.properties?.ai_summary || '')

const hasImages = computed(() =>
  (feature.value?.properties?.resources || []).some(r => r.file_type === 'image')
)

const hasPendingSummary = computed(() => aiEnabled.value && hasImages.value && !featureSummary.value)

const SUMMARY_POLL_INTERVAL = 4000
const SUMMARY_POLL_MAX_ATTEMPTS = 45

let summaryPollTimer = null
let summaryPollAttempts = 0

const stopSummaryPolling = () => {
  if (summaryPollTimer) {
    clearInterval(summaryPollTimer)
    summaryPollTimer = null
  }
}

const startSummaryPolling = () => {
  stopSummaryPolling()
  summaryPollAttempts = 0
  summaryPollTimer = setInterval(async () => {
    summaryPollAttempts += 1
    await store.dispatch('geo/fetchFeature', featureCode.value)
    if (!hasPendingSummary.value) {
      stopSummaryPolling()
    } else if (summaryPollAttempts >= SUMMARY_POLL_MAX_ATTEMPTS) {
      stopSummaryPolling()
      toast({
        title: 'Summary still processing',
        description: 'The AI summary is taking longer than expected. Refresh the page to check again.',
      })
    }
  }, SUMMARY_POLL_INTERVAL)
}

watch(hasPendingSummary, (pending) => {
  if (pending) {
    startSummaryPolling()
  } else {
    stopSummaryPolling()
  }
}, { immediate: true })

onUnmounted(stopSummaryPolling)

watch(featureCode, (code) => {
  if (feature.value) {
    fetchGeofenceEvents()
  }
}, { immediate: true })

watch(feature, (newFeature) => {
  if (newFeature) {
    fetchGeofenceEvents()

    if (map.value && route.query.skipZoom !== 'true') {
      focusOnFeature()
    }
  }
}, { immediate: true })

watch(map, (newMap) => {
  if (newMap && feature.value && route.query.skipZoom !== 'true') {
    focusOnFeature()
  }
}, { immediate: true })
</script>

<template>
  <div v-if="feature" :key="featureCode">
    <Teleport to="#panel-actions" defer>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" class="w-8 h-8 p-0 rounded-full" title="Feature actions">
            <Ellipsis class="w-5 h-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-48">
          <DropdownMenuGroup>
            <DropdownMenuItem @click="focusOnFeature" class="cursor-pointer">
              <Eye class="w-4 h-4 mr-2" />
              View on Map
            </DropdownMenuItem>
            <DropdownMenuItem @click="showLeftDialog" class="cursor-pointer">
              <Pencil class="w-4 h-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem @click="addToDataTab" class="cursor-pointer" :disabled="isAddingToDataTab">
              <Layers class="w-4 h-4 mr-2" />
              {{ isAddingToDataTab ? 'Adding...' : 'Add to Data' }}
            </DropdownMenuItem>
            <DropdownMenuItem @click="copyUrl" class="cursor-pointer">
              <Share class="w-4 h-4 mr-2" />
              Share
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DialogConfirm
              ref="dialogDeleteConfirm"
              @confirm="deleteReport"
              title="Delete report"
              description="Are you sure you want to delete this report?"
            >
              <DropdownMenuItem class="cursor-pointer text-red-600 focus:text-red-600" @select.prevent>
                <Trash2 class="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DialogConfirm>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </Teleport>

    <div v-if="feature.properties" class="px-3 py-2">
      <div class="flex items-center mb-2">
        <FolderDot class="mr-0.5 w-4 h-4 text-muted-foreground" />
        <span class="text-muted-foreground">{{ projectName }}</span>
      </div>

      <div class="mb-3">
        <div class="flex items-center ">
          <div class="mr-2 w-8 h-8 rounded-full shadow-sm flex items-center justify-center" :style="{'background-color': feature.properties.color}">
            <div v-if="feature.properties?.s3_icon">
              <img class="object-contain w-5 h-5" :src="feature.properties?.s3_icon" alt="">
            </div>
            <div v-else>
              <Point v-if="iconName === 'point'" class="fill-white" />
              <LineString v-if="iconName === 'linestring'" class="fill-white" />
              <Polygon v-if="iconName === 'polygon'" class="fill-white" />
              <Polygon v-if="iconName === 'geometrycollection'" class="fill-white" />
              <MultiPolygon v-if="iconName === 'multipolygon'" class="fill-white" />
            </div>
          </div>
          <h3 class="font-bold text-lg">{{ feature.properties.name }}</h3>
        </div>
      </div>
      <p v-if="feature.properties.description">
        {{ feature.properties.description }}
      </p>

      <div v-if="feature.properties.tags && feature.properties.tags.length > 0" class="mb-3">
        <h3 class="text-sm font-semibold mb-1">Tags</h3>
        <div class="flex flex-wrap gap-1">
          <Badge v-for="(tag, index) in feature.properties.tags" :key="index" variant="outline" class="text-xs">
            {{ tag.k }}: {{ tag.v }}
          </Badge>
        </div>
      </div>

      <div v-if="feature.properties.shared" class="mb-3">
        <div class="flex items-center">
          <Badge variant="secondary" class="text-xs">Public</Badge>
        </div>
      </div>

      <ul class="text-muted-foreground mb-4">
        <li>Created: {{ createdAt }}</li>
        <li>Synced: {{ syncedAt }}</li>
        <li class="mb-1" v-if="feature.properties.updated_at">Updated: {{ updatedAt }}</li>
        <!-- <li class="mt-1">Visible: {{ feature.properties.shared ? 'For all in project' : 'Only for me' }}</li> -->
        <li>Geometry: {{ feature.geometry.type }}</li>
        <li v-if="areaSize">Size in km²: {{ areaSize.skm }} km²</li>
        <li v-if="areaSize">Size in ha: {{ areaSize.ha }} ha</li>

        <div class="flex justify-between items-start mt-2">
          <div class="flex justify-start items-center">
            <Avatar class="w-6 h-6">
              <AvatarImage
                v-if="feature.properties.member_avatar_url"
                :src="feature.properties.member_avatar_url"
                :alt="feature.properties.member_display_name"
                :key="feature.properties.code"
              />
              <AvatarFallback></AvatarFallback>
            </Avatar>
            <span class="ml-2">{{ feature.properties.member_display_name }}</span>
            <VerifiedMarker v-if="feature.properties.member_verified" class="ml-1 flex-shrink-0" />
          </div>
        </div>
      </ul>

      <div class="mb-4" v-if="feature.properties?.geofence">
        <div>
          <h3 class="text-base font-bold flex">Virtual Geofence</h3>
          <ul class="text-muted-foreground">
            <li>Events: <span class="uppercase">{{ feature.properties.geofence_events.join(', ') }}</span></li>
            <li>Is user prompted: {{ feature.properties.geofence_prompt ? 'Yes' : 'No' }}</li>
          </ul>
          <div class="mt-1">
            <GeofenceEventList v-if="geofenceEvents?.length > 0" :events="geofenceEvents" />
            <div v-else>No any events</div>
          </div>
        </div>
      </div>

      <Monitoring
        v-if="feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon'"
        :feature="feature"
        :key="feature.properties.code"
      />

      <div class="mb-4">
        <h3 class="mb-1 text-base font-bold">Collected data</h3>
        <ReportDataList v-if="feature.properties.data?.length > 0" :data="feature.properties.data" />
        <div v-else>No collected data</div>
      </div>

      <!-- Media resources - unified display for all types -->
      <div v-if="feature.properties?.resources && feature.properties.resources.length > 0" class="mb-4">
        <h3 class="text-base font-bold mb-2">Media</h3>
        <MediaList
          :resources="feature.properties.resources"
          :show-delete="true"
          :delete-handler="handleResourceDelete"
        />
      </div>

      <!-- AI image summary -->
      <div v-if="aiEnabled && featureSummary" class="mb-4">
        <h3 class="text-base font-bold flex items-center gap-1.5 mb-2">
          <Sparkles class="h-4 w-4 text-primary" />
          Summary
        </h3>
        <p class="text-sm text-muted-foreground whitespace-pre-line rounded-lg bg-muted/50 p-3">
          {{ featureSummary }}
        </p>
      </div>
      <div v-else-if="hasPendingSummary" class="mb-4">
        <h3 class="text-base font-bold flex items-center gap-1.5 mb-2">
          <Sparkles class="h-4 w-4 text-primary" />
          Summary
        </h3>
        <p class="text-sm text-muted-foreground rounded-lg bg-muted/50 p-3">Creating</p>
      </div>

      <div class="mb-4">
        <FeatureThread
          :feature-code="feature.properties.code"
          :project-id="feature.properties.project_id"
          :key="feature.properties.code"
        />
      </div>

      <div class="mb-4" v-if="feature.geometry.type === 'Point' || (feature.geometry.type === 'LineString' && feature.geometry.coordinates.length <= 10)">
        <div>
          <div class="flex items-center justify-start">
            <h3 class="mb-1 text-base font-bold">Elevation</h3>
          </div>
          <Elevation :geometry="feature.geometry" :key="feature.properties.code" />
        </div>
      </div>

      <div class="mb-4">
        <div>
          <div class="flex items-center justify-start">
            <h3 class="mb-1 text-base font-bold">Weather</h3>
          </div>
          <Weather :code="feature.properties.code" :key="feature.properties.code" />
        </div>
      </div>

      <div class="mb-4 text-muted-foreground">
        <h3 class="mb-0 font-bold text-base">Other</h3>
        <div class="mb-2">ID: {{ feature.properties.code }}</div>

        <Button size="sm" variant="secondary" @click="exportGeojson">Export Geojson</Button>
      </div>
    </div>

  </div>
</template>
